import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useSeoFetch } from '@/hooks/useSeoFetch';
import { supabase } from '@/lib/supabase';
import { FetchApiIds } from '@/types';
import seoAuditsService from '@/services/seoAudits';
import { SeoAudit, SeoAuditIssue, SeoAuditPage } from '@/types/api/seo';
import SeoAuditDetailComponent from './SeoAuditDetail.component';

type IssuesPaginated = { data: SeoAuditIssue[]; count: number; currentPage: number; lastPage: number; nextPage: number | null; prevPage: number | null };
type PagesPaginated = { data: SeoAuditPage[]; count: number; currentPage: number; lastPage: number; nextPage: number | null; prevPage: number | null };

const SeoAuditDetailPage = function (): JSX.Element {
  const { auditId } = useParams();

  const { data: auditData, isLoading, isError, refetch: refetchAudit } = useSeoFetch<SeoAudit>(
    [FetchApiIds.SEO_AUDIT_BY_ID, auditId],
    () => seoAuditsService.getById(auditId as string),
    { enabled: !!auditId }
  );

  const resolvedAuditId = auditData?.id;

  const { data: issuesData, refetch: refetchIssues } = useSeoFetch<IssuesPaginated>(
    [FetchApiIds.SEO_AUDIT_ISSUES, resolvedAuditId],
    () => seoAuditsService.getIssues({ auditId: resolvedAuditId as string, page: 1, take: 200 }),
    { enabled: !!resolvedAuditId }
  );

  const { data: pagesData, refetch: refetchPages } = useSeoFetch<PagesPaginated>(
    [FetchApiIds.SEO_AUDIT_PAGES, resolvedAuditId],
    () => seoAuditsService.getPages({ auditId: resolvedAuditId as string, page: 1, take: 200 }),
    { enabled: !!resolvedAuditId }
  );

  // Poll for running audits
  const [liveAudit, setLiveAudit] = useState<SeoAudit | null>(null);

  useEffect(() => {
    if (!auditData) return;
    setLiveAudit(auditData);

    const isRunning = auditData.status === 'queued' || auditData.status === 'running';
    if (!isRunning) return;

    const interval = setInterval(async () => {
      try {
        const fresh = await seoAuditsService.getAuditStatus(auditData.id);
        setLiveAudit(fresh);
        if (fresh.status !== 'queued' && fresh.status !== 'running') {
          clearInterval(interval);
          refetchAudit();
          refetchIssues();
          refetchPages();
        }
      } catch {
        // Ignore poll errors
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [auditData, refetchAudit, refetchIssues, refetchPages]);

  const audit = liveAudit ?? auditData ?? null;
  const issues = issuesData?.data ?? [];
  const pages = pagesData?.data ?? [];

  const handleResolveIssue = useCallback(async (issueId: string) => {
    const { data: { user } } = await supabase.auth.getUser();
    await seoAuditsService.resolveIssue(issueId, user?.id ?? '');
    refetchIssues();
  }, [refetchIssues]);

  return (
    <SeoAuditDetailComponent
      audit={audit}
      issues={issues}
      pages={pages}
      isLoading={isLoading}
      notFound={isError}
      onResolveIssue={handleResolveIssue}
    />
  );
};

export default SeoAuditDetailPage;
